// import { advertisementJobDetailsService } from '@/api/services/advertisements/advertisementsService'
import displayError from '@/api/errors/errorResponse'
import {
  IAdvertisementResponse,
  saveAdvertisementService,
  singleAdvertisementDetailsService
} from '@/api/services/advertisements/advertisementsService'
import { Button } from '@/components/ui/common/Button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/dialog/AlertDialog'
import { cn } from '@/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { LoaderCircleIcon, X } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import DisplayPick from '../../Viewers/_components/DisplayPick'
import EditGenerator from './EditGenerator'
import { FormSchema } from './generatorFormSchema'

const SingleAdvertisement = ({
  id,
  onSubmit,
  setResultIds,
  jobData,
  setJobData,
  multipleResults
}: {
  id: string
  onSubmit: (data: Partial<FormSchema>) => void
  setResultIds: (ids: string[]) => void
  jobData: null | IAdvertisementResponse['job']
  setJobData: (data: IAdvertisementResponse['job'] | null) => void
  multipleResults: boolean
}) => {
  const [isSaved, setIsSaved] = useState(false)
  const [refetchHelper, setRefetchHelper] = useState(false)

  const { data, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: ['advertisement', id],
    queryFn: () => singleAdvertisementDetailsService(id),
    enabled: !!id,
    select: (data) => {
      if (data.success) {
        if (!jobData && data?.data?.job) setJobData(data.data.job)
        return data.data
      }
      return undefined
    },
    refetchOnWindowFocus: false
  })

  const { mutate: saveImage } = useMutation({
    mutationFn: () => saveAdvertisementService(data!.id),
    onSuccess: (res) => {
      if (!res.success) return displayError(res)
      setIsSaved(true)
    }
  })

  useEffect(() => {
    if (!data) return
    const isPending =
      data.generationStatus === 'PENDING' ||
      data.generationStatus === 'BACKGROUND_GENERATED'
    if (isPending) {
      setTimeout(async () => {
        await refetch()
        setRefetchHelper((prev) => !prev)
      }, 10000)
    }
  }, [refetchHelper, data])

  if (
    isLoading ||
    isPending ||
    data?.generationStatus === 'PENDING' ||
    data?.generationStatus === 'BACKGROUND_GENERATED'
  ) {
    return (
      <div className="group relative flex h-full min-h-96 items-center justify-center rounded-md border border-border bg-white p-1 shadow-sm">
        <LoaderCircleIcon className="animate-spin" />
      </div>
    )
  }
  return (
    <div className="group relative flex h-full min-h-96 flex-col items-center justify-center rounded-md border border-border p-1">
      {data?.resultImageFileId || isError ? (
        <>
          <img
            alt="Wygenerowana reklama"
            className="h-full w-full rounded-md object-contain"
            src={`${import.meta.env.VITE_API_URL}/api/v1/files/${data?.resultImageFileId}`}
          />
          <div
            className={cn(
              'bottom-0 mt-2 grid w-full items-center justify-center gap-2 rounded-b-md border-t-2 border-t-border bg-white px-2 py-2 transition-all duration-300 ease-in-out group-hover:opacity-100 md:absolute md:mt-0 md:py-0 md:opacity-0',
              multipleResults ? 'md:h-28 md:grid-cols-2' : 'md:h-14 md:grid-cols-4'
            )}
          >
            <Button disabled={isSaved} onClick={() => saveImage()}>
              {isSaved ? 'Grafika zapisana' : 'Zapisz grafikę'}{' '}
            </Button>
            <EditGenerator id={data!.id} setResultIds={setResultIds} />
            <Button
              onClick={() =>
                onSubmit({
                  productImageFileTmp: data!.job.productImageFileId,
                  shopLogoFileTmp: data!.job.shopLogoFileId,
                  templateFileTmp: data!.job.templateFileId,
                  resolution: [data!.job.resolution]
                })
              }
            >
              Generuj ponownie
            </Button>
            <DisplayPick id={id} />
          </div>
        </>
      ) : (
        'Wystąpił błąd podczas generowania'
      )}
    </div>
  )
}

function ResultPopup({
  resultIds,
  setResultIds,
  onSubmit,
  jobData,
  setJobData
}: {
  resultIds: string[]
  setResultIds: (ids: string[]) => void
  onSubmit: (data: Partial<FormSchema>) => void
  jobData: null | IAdvertisementResponse['job']
  setJobData: (data: IAdvertisementResponse['job'] | null) => void
}) {
  // useEffect(() => {
  //   if (!isLoading && !isPending && isError) {
  //     displayError(data as ErrorResponse)
  //     setResultId('')
  //   }
  // }, [isError])
  return (
    <AlertDialog open={!!resultIds?.length}>
      <AlertDialogContent
        aria-describedby="generowane-reklamy"
        aria-description="generowane-reklamy"
        className="h-full"
        id={'generowane-reklamy'}
      >
        <AlertDialogHeader className="flex flex-row items-center justify-between">
          <AlertDialogTitle>
            <span className="sr-only">Wynik</span>
          </AlertDialogTitle>
          <AlertDialogCancel
            className="m-0 border-0 p-0 shadow-none ring-0"
            onClick={() => setResultIds([])}
          >
            <X />
          </AlertDialogCancel>
        </AlertDialogHeader>
        <div
          className={cn(
            'grid h-full max-h-screen w-full overflow-x-hidden overflow-y-auto',
            resultIds.length > 1 ? 'grid-cols-2 gap-10' : 'grid-cols-1'
          )}
        >
          {resultIds.map((id) => (
            <SingleAdvertisement
              id={id}
              jobData={jobData}
              key={id}
              multipleResults={resultIds.length > 1}
              onSubmit={onSubmit}
              setJobData={setJobData}
              setResultIds={setResultIds}
            />
          ))}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default memo(ResultPopup)
